import { dashboardRequest } from "./client";

type DashboardCollectionPage<T> = {
  data?: T[];
  total?: number;
  has_more?: boolean;
  next_cursor?: string | null;
};

type DashboardRequest = <T>(path: string) => Promise<T>;

function withPageQuery(
  path: string,
  pageSize: number,
  offset: number,
  cursor: string | null,
): string {
  const [base, existing = ""] = path.split("?", 2);
  const query = new URLSearchParams(existing);
  query.set("limit", String(pageSize));
  if (cursor) {
    query.set("cursor", cursor);
    query.delete("offset");
  } else {
    query.set("offset", String(offset));
  }
  return `${base}?${query.toString()}`;
}

function itemKey(value: unknown, index: number): string {
  if (typeof value === "object" && value !== null) {
    const item = value as Record<string, unknown>;
    const key =
      item.id ??
      item.source_id ??
      item.PK ??
      item.SK ??
      item.storage_path ??
      item.s3_key;
    if (key != null) return String(key);
  }
  return `page-item-${index}-${JSON.stringify(value)}`;
}

export async function fetchAllDashboardPages<T>(
  path: string,
  options: {
    pageSize?: number;
    maxPages?: number;
    request?: DashboardRequest;
  } = {},
): Promise<T[]> {
  const pageSize = options.pageSize ?? 100;
  const maxPages = options.maxPages ?? 100;
  const request = options.request ?? dashboardRequest;
  const collected = new Map<string, T>();
  let offset = 0;
  let cursor: string | null = null;
  const visitedCursors = new Set<string>();
  let complete = false;

  for (let pageIndex = 0; pageIndex < maxPages; pageIndex += 1) {
    const page: DashboardCollectionPage<T> = await request<
      DashboardCollectionPage<T>
    >(withPageQuery(path, pageSize, offset, cursor));
    const rows: T[] = page.data ?? [];
    const before = collected.size;
    rows.forEach((row, rowIndex) =>
      collected.set(itemKey(row, offset + rowIndex), row),
    );

    if (rows.length === 0) {
      complete = true;
      break;
    }
    const nextCursor: string | null = page.next_cursor ?? null;
    if (nextCursor) {
      if (visitedCursors.has(nextCursor)) {
        throw new Error(`Dashboard pagination repeated cursor ${nextCursor}.`);
      }
      visitedCursors.add(nextCursor);
      cursor = nextCursor;
      continue;
    }

    offset += rows.length;
    if (
      page.has_more === false ||
      (typeof page.total === "number" && offset >= page.total) ||
      (page.has_more !== true && page.total == null)
    ) {
      complete = true;
      break;
    }
    if (collected.size === before) {
      throw new Error(
        `Dashboard pagination made no progress at offset ${offset}.`,
      );
    }
  }

  if (!complete) {
    throw new Error(
      `Dashboard pagination exceeded ${maxPages} pages for ${path}.`,
    );
  }

  return [...collected.values()];
}
